import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import ScrollToTop from "@/components/ScrollToTop";
import { installCodeShield } from "@/utils/codeShield";

// ---------- Site (Baji website) ----------
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute, GuestRoute } from "@/components/ProtectedRoute";
import LiveChatLoader from "@/components/site/LiveChatLoader";
import LiveSupportWidget from "@/components/site/LiveSupportWidget";

/**
 * SiteChatGate — hides the floating live-chat widget on routes / states where
 * showing it would block the user's primary task:
 *   • /login, /register, /affiliate/register, /forgot-password, /reset-password
 *   • Any /profile/* path (covers profile, deposit, withdraw, KYC, 2FA, etc.)
 *   • While a game iframe modal is open (signalled by useGameLaunch via the
 *     `site:game-modal` custom event with detail.open = true/false).
 */
function SiteChatGate() {
  const { pathname } = useLocation();
  const [gameOpen, setGameOpen] = useState(false);

  useEffect(() => {
    const onGameModal = (e) => setGameOpen(!!e?.detail?.open);
    window.addEventListener("site:game-modal", onGameModal);
    return () => window.removeEventListener("site:game-modal", onGameModal);
  }, []);

  const path = (pathname || "/").toLowerCase();
  const hiddenRoutePrefixes = [
    "/login", "/register", "/affiliate/register",
    "/forgot-password", "/reset-password",
    "/profile",          // profile + deposit + withdraw + all sub-routes
    "/dashboard",        // logged-in player dashboard
  ];
  const onHiddenRoute = hiddenRoutePrefixes.some((p) => path === p || path.startsWith(p + "/"));
  // Hide on lottery game pages (WinGo/K3/5D/TRX Win) — the floating chat button
  // was overlapping the game controls & pagination. `/lottery` (hub) still shows it.
  const onLotteryGame = path.startsWith("/lottery/");
  if (onHiddenRoute || onLotteryGame || gameOpen) return null;
  return <LiveSupportWidget />;
}
import MaintenanceGate from "@/components/site/MaintenanceGate";
import PopupBanner from "@/components/site/PopupBanner";
import SplashScreen from "@/components/site/SplashScreen";
import SiteNoticeBanner from "@/components/site/SiteNoticeBanner";

import HomePage from "@/pages/HomePage";
import CategoryPage from "@/pages/CategoryPage";
import LotteryHub from "@/pages/lottery/LotteryHub";
import LotteryGamePage from "@/pages/lottery/LotteryGamePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import NotificationsPage from "@/pages/profile/NotificationsPage";
import PersonalInfoPage from "@/pages/profile/PersonalInfoPage";
import LoginSecurityPage from "@/pages/profile/LoginSecurityPage";
import TwoFactorPage from "@/pages/profile/TwoFactorPage";
import VerificationPage from "@/pages/profile/VerificationPage";
import TransactionRecordsPage from "@/pages/profile/TransactionRecordsPage";
import BettingRecordsPage from "@/pages/profile/BettingRecordsPage";
import TurnoverPage from "@/pages/profile/TurnoverPage";
import FreeSpinPage from "@/pages/profile/FreeSpinPage";
import MyVipPage from "@/pages/profile/MyVipPage";
import MyReferralPage from "@/pages/profile/MyReferralPage";
import AffiliatePage from "@/pages/profile/AffiliatePage";
import AffiliateApplyPage from "@/pages/profile/AffiliateApplyPage";
import DepositPage from "@/pages/profile/DepositPage";
import WithdrawalPage from "@/pages/profile/WithdrawalPage";
import EmailVerifyPage from "@/pages/profile/EmailVerifyPage";
import PhoneVerifyPage from "@/pages/profile/PhoneVerifyPage";
import KycSubmitPage from "@/pages/profile/KycSubmitPage";
import DepositReturnPage from "@/pages/profile/DepositReturnPage";
import CryptoCashierPage from "@/pages/profile/CryptoCashierPage";
import DownloadAppPage from "@/pages/DownloadAppPage";
import BecomeAgentPage from "@/pages/BecomeAgentPage";
import AgentApplyReturn from "@/pages/AgentApplyReturn";
import {
  PromotionsPage,
  VipPage,
  ReferralPage,
  AppDownloadPage,
  ContactPage,
  AmbassadorsPage,
} from "@/pages/InfoPages";
import {
  AboutUsPage,
  PrivacyPolicyPage,
  TermsPage,
  ResponsibleGamingPage,
  KYCPage,
  ForumPage,
  NewMemberGuidePage,
} from "@/pages/StaticPages";

// ---------- Admin Panel ----------
import { AuthProvider as AdminAuthProvider } from "@/admin/context/AuthContext";
import AdminProtectedRoute from "@/admin/components/ProtectedRoute";
import DashboardLayout from "@/admin/components/Layout/DashboardLayout";

// ---------- Agent Panel ----------
import AgentApp from "@/agent/AgentApp";

// ---------- Promo & Affiliate Panel ----------
import PromoApp from "@/promo/PromoApp";

import AdminLogin from "@/admin/pages/Login";
import AdminDashboard from "@/admin/pages/Dashboard";
import Players from "@/admin/pages/Players";
import Deposits from "@/admin/pages/Deposits";
import Withdraws from "@/admin/pages/Withdraws";
import Transactions from "@/admin/pages/Transactions";
import Bets from "@/admin/pages/Bets";
import StuckBets from "@/admin/pages/StuckBets";
import AviatorCancels from "@/admin/pages/AviatorCancels";
import Games from "@/admin/pages/Games";
import Providers from "@/admin/pages/Providers";
import ConfigHomeShelves from "@/admin/pages/ConfigHomeShelves";
import Agents from "@/admin/pages/Agents";
import CreateAgent from "@/admin/pages/CreateAgent";
import PrePayment from "@/admin/pages/PrePayment";
import Commission from "@/admin/pages/Commission";
import MonthlyCommissionRun from "@/admin/pages/MonthlyCommissionRun";
import AgentSelfDepositDashboard from "@/admin/pages/AgentSelfDepositDashboard";
import Payments from "@/admin/pages/Payments";
import CreatePayment from "@/admin/pages/CreatePayment";
import Affiliate from "@/admin/pages/Affiliate";
import AffiliateProgram from "@/admin/pages/AffiliateProgram";
import AffiliateWithdraw from "@/admin/pages/AffiliateWithdraw";
import AffiliateAccounts from "@/admin/pages/AffiliateAccounts";
import AffiliateSettlements from "@/admin/pages/AffiliateSettlements";
import AffiliateAnalytics from "@/admin/pages/AffiliateAnalytics";
import AffiliateFraud from "@/admin/pages/AffiliateFraud";
import AffiliateOverview from "@/admin/pages/AffiliateOverview";
import AffiliateCampaigns from "@/admin/pages/AffiliateCampaigns";
import AffiliateCommissionTiers from "@/admin/pages/AffiliateCommissionTiers";
import AffiliateContests from "@/admin/pages/AffiliateContests";
import AffiliateTickets from "@/admin/pages/AffiliateTickets";
import AffiliateBroadcast from "@/admin/pages/AffiliateBroadcast";
import AffiliateAuditLog from "@/admin/pages/AffiliateAuditLog";
import AffiliateReports from "@/admin/pages/AffiliateReports";
import AffiliateFunnel from "@/admin/pages/AffiliateFunnel";
import AffiliatePayoutConfig from "@/admin/pages/AffiliatePayoutConfig";
import AffiliateAnnouncements from "@/admin/pages/AffiliateAnnouncements";
import AffiliateSubTree from "@/admin/pages/AffiliateSubTree";
import AffiliateCalendarCycles from "@/admin/pages/AffiliateCalendarCycles";
import AffiliateSafeConfig from "@/admin/pages/AffiliateSafeConfig";
import AffiliateDeficitTracker from "@/admin/pages/AffiliateDeficitTracker";
import Popular from "@/admin/pages/Popular";
import AddPopular from "@/admin/pages/AddPopular";
import Banners from "@/admin/pages/Banners";
import AuthBanners from "@/admin/pages/AuthBanners";
import AntiFraud from "@/admin/pages/AntiFraud";
import Popups from "@/admin/pages/Popups";
import Notifications from "@/admin/pages/Notifications";
import Support from "@/admin/pages/Support";
import LiveChat from "@/admin/pages/LiveChat";
import LiveSupportAgents from "@/admin/pages/LiveSupportAgents";
import LiveSupportTopics from "@/admin/pages/LiveSupportTopics";
import LiveSupportForms from "@/admin/pages/LiveSupportForms";
import LiveSupportSubmissions from "@/admin/pages/LiveSupportSubmissions";
import LiveSupportAgentPerformance from "@/admin/pages/LiveSupportAgentPerformance";
import LiveSupportQuickReplies from "@/admin/pages/LiveSupportQuickReplies";
import LiveSupportPerformance from "@/admin/pages/LiveSupportPerformance";
import LiveSupportChatTemplates from "@/admin/pages/LiveSupportChatTemplates";
import Reports from "@/admin/pages/Reports";
import SMSGateway from "@/admin/pages/SMSGateway";
import EmailGateway from "@/admin/pages/EmailGateway";
import NotificationEmailGateway from "@/admin/pages/NotificationEmailGateway";
import AgentEmailGateway from "@/admin/pages/AgentEmailGateway";
import AffiliateEmailGateway from "@/admin/pages/AffiliateEmailGateway";
import KycReview from "@/admin/pages/KycReview";
import AdminProfile from "@/admin/pages/Profile";
import ChangePassword from "@/admin/pages/ChangePassword";
import TwoFactor from "@/admin/pages/TwoFactor";
import ConfigGeneral from "@/admin/pages/ConfigGeneral";
import ConfigApk from "@/admin/pages/ConfigApk";
import ConfigTelegram from "@/admin/pages/ConfigTelegram";
import ConfigDST from "@/admin/pages/ConfigDST";
import ConfigLiveChat from "@/admin/pages/ConfigLiveChat";
import ConfigAIChat from "@/admin/pages/ConfigAIChat";
import ConfigTelegramSupport from "@/admin/pages/ConfigTelegramSupport";
import ConfigLogo from "@/admin/pages/ConfigLogo";
import ConfigRisk from "@/admin/pages/ConfigRisk";
import ConfigDeposit from "@/admin/pages/ConfigDeposit";
import ConfigWithdraw from "@/admin/pages/ConfigWithdraw";
import ConfigAnnouncements from "@/admin/pages/ConfigAnnouncements";
import ConfigSiteNotice from "@/admin/pages/ConfigSiteNotice";
import ConfigFooter from "@/admin/pages/ConfigFooter";
import Promotions from "@/admin/pages/Promotions";
import VipTiers from "@/admin/pages/VipTiers";
import VipDashboard from "@/admin/pages/VipDashboard";
import ConfigMeta from "@/admin/pages/ConfigMeta";
import ConfigContact from "@/admin/pages/ConfigContact";
import ConfigSocial from "@/admin/pages/ConfigSocial";
import ConfigHomeContent from "@/admin/pages/ConfigHomeContent";
import Gateways from "@/admin/pages/Gateways";
import AdminUsers from "@/admin/pages/AdminUsers";
import Categories from "@/admin/pages/Categories";
import Turnover from "@/admin/pages/Turnover";
import FreeSpin from "@/admin/pages/FreeSpin";
import ConfigUsdtDeposit from "@/admin/pages/ConfigUsdtDeposit";
import ConfigAgentBdtDeposit from "@/admin/pages/ConfigAgentBdtDeposit";import ConfigCrypto from "@/admin/pages/ConfigCrypto";
import AgentUsdtDeposits from "@/admin/pages/AgentUsdtDeposits";
import AgentBdtDeposits from "@/admin/pages/AgentBdtDeposits";
import AgentTransactions from "@/admin/pages/AgentTransactions";
import MonthlyResetPage from "@/admin/pages/MonthlyResetPage";
import ReferralNetwork from "@/admin/pages/ReferralNetwork";
import CommissionSettings from "@/admin/pages/CommissionSettings";
import ConfigAgentBonus from "@/admin/pages/ConfigAgentBonus";
import AgentGuideline from "@/admin/pages/AgentGuideline";
import AdminAgentApplications from "@/admin/pages/AdminAgentApplications";
import LotteryAdmin from "@/admin/pages/LotteryAdmin";
import LiveSupportAgentApp from "@/pages/LiveSupportAgentApp";
import { SiteSettingsProvider } from "@/contexts/SiteSettingsContext";

function SiteRoutes() {
  return (
    <SiteSettingsProvider>
    <AuthProvider>
      <SplashScreen />
      <MaintenanceGate>
      <LiveChatLoader />
      <SiteChatGate />
      <PopupBanner placement="everywhere" />
      <SiteNoticeBanner />
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#1a2030",
            color: "white",
            border: "1px solid rgba(255,255,255,0.06)",
          },
        }}
      />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/sports" element={<CategoryPage />} />
        <Route path="/casino" element={<CategoryPage />} />
        <Route path="/slots" element={<CategoryPage />} />
        <Route path="/crash" element={<CategoryPage />} />
        <Route path="/table" element={<CategoryPage />} />
        <Route path="/fishing" element={<CategoryPage />} />
        <Route path="/arcade" element={<CategoryPage />} />
        <Route path="/favourite" element={<CategoryPage />} />
        <Route path="/lottery" element={<LotteryHub />} />
        <Route path="/lottery/:game" element={<LotteryGamePage />} />

        <Route path="/promotions" element={<PromotionsPage />} />
        <Route path="/vip" element={<VipPage />} />
        <Route path="/referral" element={<ReferralPage />} />
        <Route path="/app-download" element={<AppDownloadPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/ambassadors" element={<AmbassadorsPage />} />

        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/responsible" element={<ResponsibleGamingPage />} />
        <Route path="/kyc" element={<KYCPage />} />
        <Route path="/forum" element={<ForumPage />} />
        <Route path="/guide" element={<NewMemberGuidePage />} />

        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        {/* Affiliate share links point to /affiliate/register?ref=CODE — reuse
            the same RegisterPage; ref-code is auto-detected from the URL. */}
        <Route path="/affiliate/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/profile/personal-info" element={<ProtectedRoute><PersonalInfoPage /></ProtectedRoute>} />
        <Route path="/profile/login-security" element={<ProtectedRoute><LoginSecurityPage /></ProtectedRoute>} />
        <Route path="/profile/2fa" element={<ProtectedRoute><TwoFactorPage /></ProtectedRoute>} />
        <Route path="/profile/verification" element={<ProtectedRoute><VerificationPage /></ProtectedRoute>} />
        <Route path="/profile/transactions" element={<ProtectedRoute><TransactionRecordsPage /></ProtectedRoute>} />
        <Route path="/profile/bets" element={<ProtectedRoute><BettingRecordsPage /></ProtectedRoute>} />
        <Route path="/profile/turnover" element={<ProtectedRoute><TurnoverPage /></ProtectedRoute>} />
        <Route path="/profile/free-spin" element={<ProtectedRoute><FreeSpinPage /></ProtectedRoute>} />
        <Route path="/profile/vip" element={<ProtectedRoute><MyVipPage /></ProtectedRoute>} />
        <Route path="/profile/referral" element={<ProtectedRoute><MyReferralPage /></ProtectedRoute>} />
        <Route path="/profile/affiliate" element={<ProtectedRoute><AffiliatePage /></ProtectedRoute>} />
        <Route path="/profile/affiliate-apply" element={<ProtectedRoute><AffiliateApplyPage /></ProtectedRoute>} />
        <Route path="/profile/deposit" element={<ProtectedRoute><DepositPage /></ProtectedRoute>} />
        <Route path="/profile/withdraw" element={<ProtectedRoute><WithdrawalPage /></ProtectedRoute>} />
        <Route path="/profile/email-verify" element={<ProtectedRoute><EmailVerifyPage /></ProtectedRoute>} />
        <Route path="/profile/phone-verify" element={<ProtectedRoute><PhoneVerifyPage /></ProtectedRoute>} />
        <Route path="/profile/kyc" element={<ProtectedRoute><KycSubmitPage /></ProtectedRoute>} />
        <Route path="/profile/deposit/return" element={<ProtectedRoute><DepositReturnPage /></ProtectedRoute>} />
        <Route path="/profile/deposit/crypto/:id" element={<ProtectedRoute><CryptoCashierPage /></ProtectedRoute>} />
        <Route path="/download" element={<DownloadAppPage />} />
        <Route path="/profile/download" element={<DownloadAppPage />} />
        <Route path="/become-agent" element={<BecomeAgentPage />} />
        <Route path="/become-agent/return" element={<AgentApplyReturn />} />
        <Route path="/profile/become-agent" element={<BecomeAgentPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
      </MaintenanceGate>
    </AuthProvider>
    </SiteSettingsProvider>
  );
}

function AdminRoutes() {
  return (
    <AdminAuthProvider>
      <div className="admin-shell">
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="login" element={<AdminLogin />} />
          <Route
            element={
              <AdminProtectedRoute>
                <DashboardLayout />
              </AdminProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/root/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="reports" element={<Reports />} />
            <Route path="players" element={<Players />} />

            <Route path="deposits/all" element={<Deposits />} />
            <Route path="deposits/pending" element={<Deposits onlyPending />} />

            <Route path="withdraws/all" element={<Withdraws />} />
            <Route path="withdraws/pending" element={<Withdraws onlyPending />} />

            <Route path="transactions" element={<Transactions />} />

            <Route path="bets/pending" element={<Bets onlyPending />} />
            <Route path="bets/stuck" element={<StuckBets />} />
            <Route path="bets/list" element={<Bets />} />
            <Route path="bets/aviator-cancels" element={<AviatorCancels />} />
            <Route path="bets/games" element={<Games />} />
            <Route path="bets/providers" element={<Providers />} />
            <Route path="config/home-shelves" element={<ConfigHomeShelves />} />

            <Route path="agents/create" element={<CreateAgent />} />
            <Route path="agents/list" element={<Agents />} />
            <Route path="agents/pre-payment" element={<PrePayment />} />
            <Route path="agents/commission" element={<Commission />} />
            <Route path="agents/monthly-commission-run" element={<MonthlyCommissionRun />} />
            <Route path="agents/self-deposit-dashboard" element={<AgentSelfDepositDashboard />} />
            <Route path="agents/usdt-deposits" element={<AgentUsdtDeposits />} />
            <Route path="agents/bdt-deposits" element={<AgentBdtDeposits />} />
            <Route path="agents/transactions" element={<AgentTransactions />} />
            <Route
              path="agents/deposits/all"
              element={
                <AgentTransactions
                  presetType="deposit"
                  title="All Agent Deposits"
                  subtitle="Every Agent → User deposit across the platform (all statuses)."
                  testId="admin-agent-deposits-all-page"
                />
              }
            />
            <Route
              path="agents/deposits/pending"
              element={
                <AgentTransactions
                  presetType="deposit"
                  presetStatus="pending"
                  title="Pending Agent Deposits"
                  subtitle="Agent → User deposits awaiting completion."
                  testId="admin-agent-deposits-pending-page"
                />
              }
            />
            <Route
              path="agents/withdraws/all"
              element={
                <AgentTransactions
                  presetType="withdrawal"
                  title="All Agent Withdrawals"
                  subtitle="Every User → Agent withdrawal across the platform (all statuses)."
                  testId="admin-agent-withdraws-all-page"
                />
              }
            />
            <Route
              path="agents/withdraws/pending"
              element={
                <AgentTransactions
                  presetType="withdrawal"
                  presetStatus="pending"
                  title="Pending Agent Withdrawals"
                  subtitle="User → Agent withdrawals awaiting agent action."
                  testId="admin-agent-withdraws-pending-page"
                />
              }
            />
            <Route path="agents/deposit-bonus" element={<ConfigAgentBonus />} />
            <Route path="agents/guideline" element={<AgentGuideline />} />
            <Route path="agents/applications" element={<AdminAgentApplications />} />
            <Route path="system/monthly-reset" element={<MonthlyResetPage />} />
            <Route path="referrals" element={<ReferralNetwork />} />
            <Route path="commission-settings" element={<CommissionSettings />} />

            <Route path="payment/create" element={<CreatePayment />} />
            <Route path="payment/list" element={<Payments />} />
            <Route path="payment/gateways" element={<Gateways />} />

            <Route path="affiliate/list" element={<Affiliate variant="list" />} />
            <Route path="affiliate/program" element={<AffiliateProgram />} />
            <Route path="affiliate/overview" element={<AffiliateOverview />} />
            <Route path="affiliate/campaigns" element={<AffiliateCampaigns />} />
            <Route path="affiliate/tiers" element={<AffiliateCommissionTiers />} />
            <Route path="affiliate/contests" element={<AffiliateContests />} />
            <Route path="affiliate/tickets" element={<AffiliateTickets />} />
            <Route path="affiliate/broadcast" element={<AffiliateBroadcast />} />
            <Route path="affiliate/audit" element={<AffiliateAuditLog />} />
            <Route path="affiliate/reports" element={<AffiliateReports />} />
            <Route path="affiliate/withdraw" element={<AffiliateWithdraw />} />
            <Route path="affiliate/accounts" element={<AffiliateAccounts />} />
            <Route path="affiliate/settlements" element={<AffiliateSettlements />} />
            <Route path="affiliate/analytics" element={<AffiliateAnalytics />} />
            <Route path="affiliate/funnel" element={<AffiliateFunnel />} />
            <Route path="affiliate/fraud" element={<AffiliateFraud />} />
            <Route path="affiliate/payout-config" element={<AffiliatePayoutConfig />} />
            <Route path="affiliate/announcements" element={<AffiliateAnnouncements />} />
            <Route path="affiliate/sub-tree" element={<AffiliateSubTree />} />
            <Route path="affiliate/cycles" element={<AffiliateCalendarCycles />} />
            <Route path="affiliate/safe-config" element={<AffiliateSafeConfig />} />
            <Route path="affiliate/deficits" element={<AffiliateDeficitTracker />} />
            <Route path="affiliate/history" element={<Affiliate variant="history" />} />
            <Route path="affiliate/transfer" element={<Affiliate variant="transfer" />} />
            <Route path="affiliate/review" element={<Affiliate variant="review" />} />

            <Route path="banners" element={<Banners />} />
            <Route path="auth-banners" element={<AuthBanners />} />
            <Route path="anti-fraud" element={<AntiFraud />} />
            <Route path="popups" element={<Popups />} />
            <Route path="popular/add" element={<AddPopular />} />
            <Route path="popular/list" element={<Popular />} />

            <Route path="notifications" element={<Notifications />} />
            <Route path="live-chat" element={<LiveChat />} />
            <Route path="live-support-agents" element={<LiveSupportAgents />} />
            <Route path="live-support-topics" element={<LiveSupportTopics />} />
            <Route path="live-support-forms" element={<LiveSupportForms />} />
            <Route path="live-support-submissions" element={<LiveSupportSubmissions />} />
            <Route path="live-support-agent-performance" element={<LiveSupportAgentPerformance />} />
            <Route path="live-support-quick-replies" element={<LiveSupportQuickReplies />} />
            <Route path="live-support-chat-templates" element={<LiveSupportChatTemplates />} />
            <Route path="live-support-performance" element={<LiveSupportPerformance />} />
            <Route path="support" element={<Support />} />

            <Route path="configuration" element={<Navigate to="/root/admin/configuration/general" replace />} />
            <Route path="configuration/general" element={<ConfigGeneral />} />
            <Route path="configuration/logo" element={<ConfigLogo />} />
            <Route path="configuration/risk" element={<ConfigRisk />} />
            <Route path="configuration/deposit" element={<ConfigDeposit />} />
            <Route path="configuration/withdraw" element={<ConfigWithdraw />} />
            <Route path="configuration/announcements" element={<ConfigAnnouncements />} />
            <Route path="configuration/site-notice" element={<ConfigSiteNotice />} />
            <Route path="configuration/footer" element={<ConfigFooter />} />
            <Route path="configuration/live-chat" element={<ConfigLiveChat />} />
            <Route path="configuration/ai-chat" element={<ConfigAIChat />} />
            <Route path="configuration/telegram-support" element={<ConfigTelegramSupport />} />
            <Route path="configuration/dst" element={<ConfigDST />} />
            <Route path="configuration/apk" element={<ConfigApk />} />
            <Route path="configuration/telegram" element={<ConfigTelegram />} />
            <Route path="promotions" element={<Promotions />} />
            <Route path="vip-tiers" element={<VipTiers />} />
            <Route path="vip-dashboard" element={<VipDashboard />} />
            <Route path="configuration/meta" element={<ConfigMeta />} />
            <Route path="configuration/contact" element={<ConfigContact />} />
            <Route path="configuration/social" element={<ConfigSocial />} />
            <Route path="configuration/home-content" element={<ConfigHomeContent />} />
            <Route path="configuration/usdt-deposit" element={<ConfigUsdtDeposit />} />
            <Route path="configuration/agent-bdt-deposit" element={<ConfigAgentBdtDeposit />} />
            <Route path="configuration/crypto" element={<ConfigCrypto />} />
            <Route path="categories" element={<Categories />} />
            <Route path="turnover" element={<Turnover />} />
            <Route path="lottery" element={<LotteryAdmin />} />
            <Route path="free-spin" element={<FreeSpin />} />
            <Route path="settings/sms" element={<SMSGateway />} />
            <Route path="settings/email" element={<EmailGateway />} />
            <Route path="settings/notification-email" element={<NotificationEmailGateway />} />
            <Route path="settings/agent-email" element={<AgentEmailGateway />} />
            <Route path="settings/affiliate-email" element={<AffiliateEmailGateway />} />
            <Route path="kyc-review" element={<KycReview />} />
            <Route path="kyc" element={<Navigate to="/root/admin/kyc-review" replace />} />

            <Route path="admin-users" element={<AdminUsers />} />

            <Route path="profile" element={<AdminProfile />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="two-factor" element={<TwoFactor />} />

            <Route path="*" element={<Navigate to="/root/admin/dashboard" replace />} />
          </Route>
        </Routes>
      </div>
    </AdminAuthProvider>
  );
}

function App() {
  // Best-effort code-protection shield. Auto-skips on /root/admin/* and /agent/*
  // (admins need DevTools). Toggle via REACT_APP_SHIELD_ENABLED env var.
  useEffect(() => {
    try { installCodeShield(); } catch { /* ignore */ }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/root/admin/*" element={<AdminRoutes />} />
          <Route path="/agent/*" element={<AgentApp />} />
          <Route path="/promo/*" element={<PromoApp />} />
          <Route path="/livechat" element={<LiveSupportAgentApp />} />
          <Route path="/livechat/*" element={<LiveSupportAgentApp />} />
          <Route path="/*" element={<SiteRoutes />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
